import { FC, useEffect, useRef, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer from 'src/components/StyledComponents/StyledGridContainer';
import { Production } from 'src/models/Management.interface';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import { getAllProductions } from 'src/services/production.service';
import { SortOrder, SortBy } from 'src/models/Sort.types';
import ProductionActionMenuButton from 'src/components/ActionButtons/ProductionActionMenu';
import { pagination } from 'src/utils/properties.utils';

interface ProductionFilters {
  productId: string;
  startDate: Date | null;
  endDate: Date | null;
}

const ProductionGrid: FC<{
  state: boolean;
  searchTerm: string;
  filters: ProductionFilters;
}> = ({ state, searchTerm, filters }) => {
  const [offset, setOffset] = useState(pagination.DEFAULT_PAGE);
  const [limit, setLimit] = useState(pagination.DEFAULT_PAGE_SIZE);
  const [productions, setProductions] = useState<Production[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<SortBy>('productionDate');
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const isFirstRender = useRef(true);
  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'productionDate', sort: 'desc' },
  ]);

  const processedProductions = productions.map((production, index) => ({
    id: production.id,
    number: (offset - 1) * limit + index + 1,
    productName: production.productName,
    productId: production.productId,
    producedQuantity: production.producedQuantity,
    damagedQuantity: production.damagedQuantity,
    productionDate: production.productionDate,
    deletedBy: production.deletedBy,
    deletedReason: production.deletedReason,
  }));

  const fetchAndSetProductions = async () => {
    setLoading(true);
    try {
      const response = await getAllProductions(
        offset,
        limit,
        searchTerm,
        sortBy,
        order,
        filters.productId,
        filters.startDate,
        filters.endDate
      );
      setProductions(response.data);
      setTotalItems(response.totalItems);
    } catch {
      setProductions([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetProductions();
  }, [sortBy, order, searchTerm, filters, offset, limit, refreshKey]);

  // Reset to page 1 whenever a new production is created (state toggles)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (offset === pagination.DEFAULT_PAGE) {
      setRefreshKey((prev) => prev + 1);
    } else {
      setOffset(pagination.DEFAULT_PAGE);
    }
  }, [state]);

  useEffect(() => {
    setOffset(pagination.DEFAULT_PAGE);
  }, [searchTerm, filters]);

  const handleRefreshAfterDelete = () => {
    if (offset === pagination.DEFAULT_PAGE) {
      setRefreshKey((prev) => prev + 1);
    } else {
      setOffset(pagination.DEFAULT_PAGE);
    }
  };

  const handleRefreshAfterEdit = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setOffset(pagination.DEFAULT_PAGE);
    setSortBy((sort?.field as SortBy) || 'productionDate');
    setOrder((sort?.sort as SortOrder) || SortOrder.DESC);
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.2,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'productName',
      headerName: 'Product Name',
      flex: 0.8,
    },
    {
      field: 'producedQuantity',
      headerName: 'Produced Quantity',
      flex: 0.8,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'damagedQuantity',
      headerName: 'Damaged Quantity',
      flex: 0.8,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'productionDate',
      headerName: 'Production Date',
      flex: 0.8,
      headerAlign: 'right',
      align: 'right',
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const production: Production = {
          id: params.row.id,
          productId: params.row.productId,
          productName: params.row.productName,
          producedQuantity: params.row.producedQuantity,
          damagedQuantity: params.row.damagedQuantity,
          productionDate: params.row.productionDate,
          deletedBy: params.row.deletedBy,
          deletedReason: params.row.deletedReason,
        };

        return (
          <ProductionActionMenuButton
            production={production}
            onEditSuccess={handleRefreshAfterEdit}
            onDeleteSuccess={handleRefreshAfterDelete}
          />
        );
      },
    },
  ];

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={offset}
            setPage={setOffset}
            pageSize={limit}
            setPageSize={setLimit}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={processedProductions}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default ProductionGrid;
